import { motion } from "framer-motion";
import { 
  CheckCircle2, 
  Shield, 
  Zap, 
  DollarSign,
  Percent,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Container, SectionHeading, SectionSubheading } from "./shared";

const features = [
  "Unlimited products",
  "Custom video player with chapters",
  "Built-in community per course",
  "Stripe Connect payouts",
  "Student management",
  "Analytics dashboard",
];

const highlights = [
  {
    icon: Shield,
    title: "Transparent Fees",
    description: "Know exactly what you'll pay. No hidden charges or surprises.",
  },
  {
    icon: Zap,
    title: "Fast Payouts",
    description: "Get paid directly via Stripe Connect. Weekly automatic payouts.",
  },
  {
    icon: DollarSign,
    title: "No Monthly Fees",
    description: "No subscription required. Only pay when you make money.",
  },
];

export function Pricing() {
  return (
    <section id="pricing" className="py-16 md:py-24 lg:py-32 bg-neutral-50 dark:bg-neutral-950/50">
      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Text Content */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="mb-2"
            >
              <span className="text-sm font-medium text-emerald-500">Pricing</span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <SectionHeading className="mb-4">
                Creator-first pricing.{" "}
                <br className="hidden sm:block" />
                Keep more of what you earn.
              </SectionHeading>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <SectionSubheading className="mb-6">
                No monthly fees. No upfront costs. We only make money when you do.
              </SectionSubheading>
            </motion.div>

            <Button variant="link" className="px-0 mb-8" asChild>
              <Link to="/pricing">View detailed pricing</Link>
            </Button>

            {/* Highlights */}
            <div className="space-y-4">
              {highlights.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + i * 0.1, duration: 0.4 }}
                  viewport={{ once: true }}
                  className="flex items-start gap-3"
                >
                  <div className="w-10 h-10 rounded-lg bg-neutral-200 dark:bg-neutral-800 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-0.5">{item.title}</h4>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Right: Single Pricing Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="rounded-2xl p-6 md:p-8 bg-neutral-100 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700"
          >
            {/* Fee Display */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/10 mb-4">
                <Percent className="w-8 h-8 text-emerald-500" />
              </div>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-6xl md:text-7xl font-bold text-foreground">5</span>
                <span className="text-3xl md:text-4xl font-bold text-foreground">%</span>
              </div>
              <p className="text-muted-foreground mt-2">per transaction</p>
              <p className="text-sm text-muted-foreground/70 mt-1">+ Stripe processing fees</p>
            </div>

            {/* Features */}
            <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6 mb-6">
              <p className="text-sm font-medium text-foreground mb-4">Everything included:</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA */}
            <Button className="w-full" size="lg" asChild>
              <Link to="/auth/signup">Start Creating — It's Free</Link>
            </Button>
            <p className="text-xs text-center text-muted-foreground mt-3">
              No credit card required
            </p>
          </motion.div>
        </div>
      </Container>
    </section>
  );
}
